import { faMagnifyingGlass, faX } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import type { AlphabetFilterProps } from '~/components/common/AlphabetFilter';
import { AlphabetFilter } from '~/components/common/AlphabetFilter';
import { Button } from '~/components/ui/button';
import { useDebounce } from '~/hooks/debounce';
import { cn } from '~/utils/common';

export interface FilterSearchProps {
  startsWithValue: string;
  includesValue: string;
  onStartsWithChange: (value: string) => any;
  onIncludesChange: (value: string) => any;
  alphabetFilterProps?: Partial<AlphabetFilterProps>;
  renderSearch?: (component: React.ReactNode) => React.ReactNode;
  stick?: boolean;
  showAlphabet?: boolean;
  searchHelpText?: ReactNode;
}

export function FilterSearch({
  startsWithValue,
  includesValue,
  onStartsWithChange,
  onIncludesChange,
  alphabetFilterProps,
  renderSearch,
  showAlphabet = true,
  searchHelpText,
}: FilterSearchProps) {
  return (
    <div className={cn('space-y-2')}>
      {showAlphabet && (
        <AlphabetFilter
          value={startsWithValue}
          onFilterChange={onStartsWithChange}
          {...alphabetFilterProps}
        />
      )}
      {renderSearch ? (
        renderSearch(<SearchBox onChange={onIncludesChange} />)
      ) : (
        <div className="space-y-0.5">
          <div className="lg:grid lg:grid-cols-12">
            <div className="lg:col-span-4 lg:col-start-5">
              <SearchBox onChange={onIncludesChange} />
            </div>
          </div>
          {searchHelpText && (
            <div className="text-center text-xs text-muted italic">
              {searchHelpText}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SearchBox({ onChange }: { onChange: (value: string) => unknown }) {
  const [inputValue, setInputValue] = useState('');
  const inputElRef = useRef<HTMLInputElement>(null);
  const inputValueDb = useDebounce(inputValue, 500);

  useEffect(() => {
    onChange(inputValueDb);
  }, [onChange, inputValueDb]);

  function handleClear() {
    setInputValue('');
    onChange('');
    if (inputElRef.current) {
      inputElRef.current.value = '';
      inputElRef.current.focus();
    }
  }

  return (
    <label className="input w-full">
      <FontAwesomeIcon icon={faMagnifyingGlass} />
      <input
        ref={inputElRef}
        type="text"
        defaultValue={inputValue}
        onChange={e => setInputValue(e.target.value)}
        placeholder="Search"
      />
      {inputValue.length ? (
        <Button
          type="button"
          onClick={handleClear}
          color="ghost"
          size="xs"
          shape="circle"
        >
          <FontAwesomeIcon icon={faX} />
        </Button>
      ) : null}
    </label>
  );
}
